import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useDisclosure, Modal, ModalContent, ModalHeader, ModalBody, Tooltip, Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useGetExplainAnswerDetailsMutation } from '@circuitry-ai/doc-data';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { checkType, CtyShowToast } from '../toast/CtyToast';
import { CtyButton } from '../button/CtyButton';
import { useAgGridStatePersistence } from '../utiluicomponents/useAgGridStatePersistence';

const HARD_FILTER_KEYS = [
  'product_name',
  'product_model',
  'policy_coverage_code',
  'failure_loss_code'
];
const PARTIAL_HARD_KEY = 'product_model';
const SOFT_KEYS = ['complaint', 'cause', 'corrective_action'] as const;

const LABELS = {
  title: 'Explanation for Repair Cost',
  predictedCost: 'Predicted Cost',
  partsCost: 'Parts Cost',
  labourHours: 'Labour Hours',
  nearestMatches: 'Nearest Matches',
  basedOnClaims: (count: number) => `Predicted cost based on ${count} nearest historical claims.`,
  relaxedFilter: 'Product model filter was relaxed — fallback retrieval applied.',
  fallbackBanner: (model: string, product: string) =>
    `Fallback retrieval active: Fewer than 3 records found matching ${model}. Model filter was relaxed — results include similar ${product} claims across other model variants.`,
  inputClaim: 'Input Claim',
  hardFiltersLabel: 'Hard Filters',
  softMatchesLabel: 'Text Matches',
  nearestHistoricalClaims: 'Top Nearest Matches',
  candidates: (count: number) => `${count} candidates`,
  fallbackApplied: 'fallback applied',
  softMatchWeights: 'Soft Match Weights',
  weightsNote:
    'Weights apply to soft fields only. Hard filters narrow the candidate pool — they gate eligibility, not cost scoring.',
  totalPredicted: 'Total Predicted',
  fromTopMatch: 'from top-ranked match',
  labourRate: 'Labour Rate',
  labourCost: 'Labour Cost',
  basis: 'Basis: Cost values from the highest-scoring historical match (rank #1).',
  notAvailable: 'Explainer is not available for this prediction',
  openExplainer: 'Open prediction explainer',
  complaint: 'Complaint',
  cause: 'Cause',
  correctiveAction: 'Corrective action',
  parts: 'Parts',
  labour: 'Labour',
  topMatch: 'TOP MATCH',
  fallback: 'FALLBACK',
  exactModel: 'EXACT MODEL',
  relaxed: 'RELAXED',
  errorFetching: 'Error fetching prediction explanation',
  close: 'Close',
  totalActual: 'Total Actual',
  actualFromClaim: 'Actual cost from claim.',
  actual: 'Actual',
  predicted: 'Predicted',
  titleLossCode: 'Explanation for Loss code coverage',
  itemCode: 'Item Code',
  itemName: 'Item Name',
  notFound: 'Not Found',
  totalCostApproved: 'Total',
} as const;

/* ── helpers ─────────────────────────────────────────────────────────── */

function parse(raw: any): Record<string, any> {
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw ?? {};
  } catch {
    return {};
  }
}

function fmt(val: number | null | undefined, prefix = '$'): string {
  if (val == null) return '—';
  return `${prefix}${Number(val).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtHrs(val: number | null | undefined): string {
  if (val == null) return '—';
  return `${Number(val).toFixed(1)} hrs`;
}

function getScoreColorClasses(score: number, isTop: boolean): { text: string; bar: string } {
  if (isTop || score >= 0.63) return { text: 'text-green-600', bar: 'bg-green-600' };
  if (score >= 0.55) return { text: 'text-amber-600', bar: 'bg-amber-600' };
  return { text: 'text-default-500', bar: 'bg-gray-500' };
}

function detectFallback(question: Record<string, any>, topMatches: any[]): boolean {
  return topMatches.some((m) => {
    const c = parse(m.page_content);
    return (
      (c.product_name || '').toUpperCase() !== (question.product_name || '').toUpperCase() ||
      (c.product_model || '').toUpperCase() !== (question.product_model || '').toUpperCase()
    );
  });
}

/** Format key names for display: no underscores, title case (e.g. product_model → Product model) */
function formatLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
}

/** Chunk / question may use `complaint` or `complaint_description` (same for cause, corrective_action). */
function softFieldText(obj: Record<string, any>, baseKey: 'complaint' | 'cause' | 'corrective_action'): string {
  const v = obj[baseKey] || obj[`${baseKey}_description`];
  if (v == null) return '';
  return String(v);
}

/** `parts`, `part`, or `part_descriptions` as array or pipe-separated string → comma-separated display. */
function formatPartsListFromChunk(c: Record<string, any>): string {
  const raw = c.parts ?? c.part ?? c.part_descriptions ?? c.parts_description;
  if (raw == null) return '';
  if (Array.isArray(raw)) {
    return raw.map((x) => (x == null ? '' : String(x).trim())).filter(Boolean).join(', ');
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return '';
    if (s.includes('|')) return s.split('|').map((x) => x.trim()).filter(Boolean).join(', ');
    return s;
  }
  return String(raw);
}

/* ── sub-components ──────────────────────────────────────────────────── */

interface FilterPillProps {
  label: string;
  value: string;
  relaxed?: boolean;
  relaxedLabel: string;
}

const FilterPill: React.FC<FilterPillProps> = ({ label, value, relaxed, relaxedLabel }) => (
  <div className="mb-2">
    <div className="text-xs text-default-500  mb-0.5 tracking-wide">
      {formatLabel(label)}
    </div>
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium  ${
        relaxed ? 'bg-amber-50 text-amber-700 border border-amber-200/80 opacity-90' : 'bg-primary/10 text-primary border border-primary/20'
      }`}
    >
      {value}
      {relaxed && (
        <span className="text-[10px] font-bold bg-amber-600 text-white px-1.5 py-0.5 rounded tracking-wide">
          {relaxedLabel}
        </span>
      )}
    </span>
  </div>
);

interface SoftBoxProps {
  label: string;
  value: string;
}

const SoftBox: React.FC<SoftBoxProps> = ({ label, value }) => (
  <div className="mb-2.5">
    <div className="text-xs text-default-500  mb-0.5 tracking-wide">
      {formatLabel(label)}
    </div>
    <div className="text-xs text-primary leading-snug bg-primary/10 border border-primary/20 rounded-md p-2 break-words prose prose-sm max-w-none [&_p]:m-0 [&_ul]:m-0 [&_ol]:m-0">
      {value ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
      ) : '—'}
    </div>
  </div>
);

interface ScoreBarProps {
  score: number;
  max?: number;
  textClass: string;
  barClass: string;
}

const ScoreBar: React.FC<ScoreBarProps> = ({ score, max = 10, textClass, barClass }) => {
  const pct = Math.min(100, (score / max) * 100);
  return (
    <div className="flex items-center gap-1">
      <span className={`text-[11px]  font-semibold min-w-[36px] text-right ${textClass}`}>
        {typeof score === 'number' && score > 1 ? score.toFixed(1) : score?.toFixed(3)}
      </span>
      <div className="w-[30px] h-1 bg-default-200 rounded overflow-hidden">
        <div className={`h-full rounded ${barClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

interface BadgeProps {
  label: string;
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({ label, className = '' }) => (
  <span className={`text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded ${className}`}>
    {label}
  </span>
);

function getConfidenceBadgeClasses(score: number): string {
  if (score >= 85) return 'bg-green-100 text-green-700';
  if (score >= 60) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
}

interface FieldLineProps {
  label: string;
  value: string;
  truncate?: boolean;
  confidenceScore?: number | null;
}

const FieldLine: React.FC<FieldLineProps> = ({ label, value, truncate, confidenceScore }) => {
  const display = truncate && value?.length > 80 ? value.slice(0, 80) + '…' : value;
  return (
    <div className="text-xs leading-snug mb-0.5 mt-1.5">
      <span className="text-default-500 font-medium mr-1">{label}</span>
      {confidenceScore != null && (
        <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded mr-1.5 ${getConfidenceBadgeClasses(confidenceScore)}`}>
          {Math.round(confidenceScore)}%
        </span>
      )}
      <span className="font-bold text-foreground">
        : <span className="prose prose-sm max-w-none inline [&_p]:inline [&_p]:m-0">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{display}</ReactMarkdown>
          </span>
      </span>
    </div>
  );
};

interface CostStatProps {
  label: string;
  value: string;
  highlight?: boolean;
}

const CostStat: React.FC<CostStatProps> = ({ label, value, highlight }) => (
  <div className="text-right">
    <div className="text-xs uppercase tracking-wider text-default-500 font-medium">{label}</div>
    <div className={`text-xs font-bold ${highlight ? 'text-green-600 dark:text-green-400' : 'text-foreground'}`}>
      {value}
    </div>
  </div>
);

interface WeightBarProps {
  label: string;
  pct: number;
  subLabel?: string;
}

const WeightBar: React.FC<WeightBarProps> = ({ label, pct, subLabel }) => (
  <div className="mb-2.5">
    <div className="flex justify-between items-center mb-1">
      <span className="text-xs text-default-700">
        {label}
        {subLabel && <span className="text-[11px] text-default-500 ml-1">{subLabel}</span>}
      </span>
      <span className="text-[11px]  font-semibold text-cyan-700 dark:text-cyan-400">
        {Math.round(pct * 100)}%
      </span>
    </div>
    <div className="h-1.5 bg-default-200 rounded overflow-hidden">
      <div className="h-full bg-primary rounded" style={{ width: `${pct * 100}%` }} />
    </div>
  </div>
);

function getMatchCardStyles(isTop: boolean, fallback: boolean) {
  if (isTop) {
    return {
      border: 'border-2 border-green-300',
      headerBg: 'bg-green-50 dark:bg-green-500/10',
      rankBadge: 'bg-green-600 text-white',
      headerBorder: 'border-green-300',
    };
  }
  if (fallback) {
    return {
      border: 'border border-amber-300',
      headerBg: 'bg-amber-50 dark:bg-amber-500/10',
      rankBadge: 'bg-amber-600 text-white',
      headerBorder: 'border-amber-300',
    };
  }
  return {
    border: 'border border-default-200',
    headerBg: 'bg-default-100',
    rankBadge: 'bg-default-300 text-default-700',
    headerBorder: 'border-default-200',
  };
}

interface MatchCardProps {
  match: any;
  rank: number;
  isTop: boolean;
  question: Record<string, any>;
  isLossCode?: boolean;
  showScore?: boolean;
}

const MatchCard: React.FC<MatchCardProps> = ({ match, rank, isTop, question, isLossCode, showScore = true }) => {
  const { t } = useTranslation();
  const c = parse(match.page_content);
  const complaintText = softFieldText(c, 'complaint');
  const causeText = softFieldText(c, 'cause');
  const correctiveText = softFieldText(c, 'corrective_action');
  const partsListText = formatPartsListFromChunk(c);
  const conf = c.confidence != null && typeof c.confidence === 'object' ? c.confidence : null;
  const claimNoStr = String(c.claim_number ?? c.claim_no ?? '').trim();
  const requestIdStr = String(c.request_id ?? '').trim();
  const score = match.confidence_score;
  const { text: textClass, bar: barClass } = getScoreColorClasses(score, isTop);
  const sameModel =
    (c.product_model || '').toUpperCase() === (question?.product_model || '').toUpperCase();
  const titleText = isLossCode && typeof match.page_content === 'string'
    ? match.page_content
    : `${c.product_model || ''} · ${c.product_name || ''}`.trim() || '—';

  const styles = getMatchCardStyles(isTop, !sameModel);

  return (
    <div className={`rounded-lg overflow-hidden bg-content1 transition-shadow ${styles.border}`}>
      <div className={`flex items-start flex-wrap gap-1 sm:gap-0 py-2 px-2 sm:px-3 ${styles.headerBg} border-b ${styles.headerBorder}`}>
        <div className={`w-auto min-h-[22px] rounded flex items-center justify-center flex-shrink-0 mr-1 sm:mr-2 p-2 text-[10px] sm:text-[11px] font-bold ${styles.rankBadge}`}>
          # {rank}
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <span className="text-[11px] sm:text-xs font-semibold text-foreground truncate">{titleText}</span>
          {!isLossCode && (
            <span className="text-[10px] sm:text-[11px] text-default-500 font-medium flex flex-wrap gap-x-2 gap-y-0">
              <span className="truncate max-w-[50%] min-w-0">
                {t('Claim')} {claimNoStr || '—'}
              </span>
              <span className="truncate max-w-[50%] min-w-0">
                {t('Request')} {requestIdStr || '—'}
              </span>
            </span>
          )}
        </div>
        {!isLossCode && (
          <div className="flex items-start flex-wrap gap-1.5 ml-2 self-start">
            {isTop && <Badge label={LABELS.topMatch} className="bg-green-600 text-white" />}
            {!sameModel && !isTop && <Badge label={LABELS.fallback} className="bg-amber-600 text-white" />}
            {sameModel && !isTop && <Badge label={LABELS.exactModel} className="bg-primary text-white" />}
            <Badge label={c.failure_loss_code || '—'} className="bg-primary/15 text-primary " />
          </div>
        )}
        {isLossCode && isTop && (
          <div className="flex items-start gap-1.5 ml-2 self-start">
            <Badge label={LABELS.topMatch} className="bg-green-600 text-white" />
          </div>
        )}
        {showScore && (
          <div className="self-start shrink-0">
            <ScoreBar score={score} max={score > 1 ? 10 : 1} textClass={textClass} barClass={barClass} />
          </div>
        )}
      </div>

      {!isLossCode && (
        <div className="py-2 px-2 sm:px-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-1 sm:gap-2">
          <div>
            {complaintText && <FieldLine label={LABELS.complaint} value={complaintText} confidenceScore={conf?.complaint} />}
            {causeText && <FieldLine label={LABELS.cause} value={causeText} confidenceScore={conf?.cause} />}
            {correctiveText && <FieldLine label={LABELS.correctiveAction} value={correctiveText} confidenceScore={conf?.corrective_action} />}
            {partsListText && <FieldLine label={t('Part descriptions')} value={partsListText} confidenceScore={conf?.parts_description} />}
          </div>
          <div className="flex sm:flex-col gap-2 sm:gap-1 sm:items-end flex-shrink-0">
            <CostStat label={LABELS.parts} value={fmt(c.parts_cost_approved_per_request_id)} />
            <CostStat label={LABELS.labourCost} value={fmt(c.labour_cost_approved_per_request_id)} />
            {c.labour_hour_credited_per_request != null && c.labour_hour_claim_per_request != null ? (
              <>
                <CostStat label={t('Labour hours (credited)')} value={fmtHrs(c.labour_hour_credited_per_request)} />
                <CostStat label={t('Labour hours (claim)')} value={fmtHrs(c.labour_hour_claim_per_request)} />
              </>
            ) : (
              <CostStat
                label={LABELS.labour}
                value={fmtHrs(c.labour_hour_credited_per_request ?? c.labour_hour_claim_per_request)}
              />
            )}
            <CostStat label={LABELS.totalCostApproved} value={fmt(c.total_cost_approved_per_request_id)} highlight={isTop} />
          </div>
        </div>
      )}
    </div>
  );
};

/* ── main 3-column layout ────────────────────────────────────────────── */

interface RepairCostExplainerContentProps {
  data: any;
  onClose: () => void;
}

const RepairCostExplainerContent: React.FC<RepairCostExplainerContentProps> = ({ data, onClose }) => {
  const { t } = useTranslation();
  const { question, answer, retrieved_contexts = [], reranked_contexts = [], time } = data;
  const retrievedContextsMessage =
    typeof retrieved_contexts === 'string' ? retrieved_contexts.trim() : '';
  const retrievedContextsList = Array.isArray(retrieved_contexts) ? retrieved_contexts : [];
  const rerankedContextsList = Array.isArray(reranked_contexts) ? reranked_contexts : [];
  const isLossCode = question?.knowledge_type === 'loss_code';
  const hasAnswerContent =
    answer != null &&
    (typeof answer !== 'string' || answer.trim() !== '');
  const hasHardFilterFields =
    question &&
    typeof question === 'object' &&
    HARD_FILTER_KEYS.some((k) => question[k] != null);
  const hasSoftMatchFields =
    question &&
    typeof question === 'object' &&
    SOFT_KEYS.some((k) => softFieldText(question, k).trim() !== '');
  const itemCode = isLossCode ? (answer?.item_code ?? '—') : '';
  const itemName = isLossCode ? (answer?.item_name ?? '—') : '';
  const answerObj = typeof answer === 'object' && answer != null ? answer : {};
  const { explain = {} } = answerObj;

  const hasReranked = rerankedContextsList.length > 0;
  const topMatches = hasReranked ? rerankedContextsList : retrievedContextsList.slice(0, 1);
  const isFallback = !isLossCode && question && detectFallback(question, topMatches);

  const topChunkIds = new Set(topMatches.map((m: any) => m.chunk_id));
  const extraMatches = retrievedContextsList
    .filter((r: any) => !topChunkIds.has(r.chunk_id))
    .slice(0, Math.max(0, 10 - topMatches.length));
  const allMatches = [...topMatches, ...extraMatches];
  const rerankedChunkIds = hasReranked ? topChunkIds : new Set<string>();

  const totalCost = answer?.total_cost_per_request;
  const labourCost = answer?.labour_cost_per_request_id;
  const partsCost = answer?.parts_cost_approved_per_request_id;
  const labourHrs = answer?.labour_hour_credited_per_request;
  const labourRate = labourHrs != null && labourHrs !== 0 && labourCost != null ? labourCost / labourHrs : null;

  const actualTotalCost = question?.actual_total_cost;
  const actualPartsCost = question?.actual_parts_cost;
  const actualLabourHrs = question?.actual_labour_hours;
  const actualLabourCost = question?.actual_labour_cost;
  const actualLabourRate = question?.labour_per_hour_cost_requested;

  const softWeights = [
    {
      key: 'corrective_action',
      label: formatLabel('corrective_action'),
      pct: explain.corrective_action_importance || 0,
      sub: '(highest weight)',
    },
    {
      key: 'complaint',
      label: formatLabel('complaint'),
      pct: explain.complaint_importance || 0,
    },
    {
      key: 'cause',
      label: formatLabel('cause'),
      pct: explain.cause_importance || 0,
    },
  ].sort((a, b) => b.pct - a.pct);

  type CostComparisonRow = { id: string; metric: string; actual: string; predicted: string };

  const costComparisonRows = useMemo((): CostComparisonRow[] => {
    return [
      { id: 'parts', metric: LABELS.partsCost, actual: fmt(actualPartsCost), predicted: fmt(partsCost) },
      { id: 'labourHrs', metric: LABELS.labourHours, actual: fmtHrs(actualLabourHrs), predicted: fmtHrs(labourHrs) },
      {
        id: 'labourRate',
        metric: LABELS.labourRate,
        actual: actualLabourRate == null ? '—' : `$${actualLabourRate.toFixed(2)}/hr`,
        predicted: labourRate == null ? '—' : `$${labourRate.toFixed(2)}/hr`,
      },
      { id: 'labourCost', metric: LABELS.labourCost, actual: fmt(actualLabourCost), predicted: fmt(labourCost) },
    ];
  }, [
    actualPartsCost,
    partsCost,
    actualLabourHrs,
    labourHrs,
    actualLabourRate,
    labourRate,
    actualLabourCost,
    labourCost,
  ]);

  const costComparisonColDefs = useMemo<ColDef<CostComparisonRow>[]>(
    () => [
      {
        field: 'metric',
        headerName: '',
        flex: 1.2,
        minWidth: 100,
        cellClass: 'text-xs text-default-700',
      },
      {
        field: 'actual',
        headerName: LABELS.actual,
        flex: 1,
        minWidth: 88,
        cellClass: 'text-xs text-left font-semibold text-foreground',
        headerClass: 'text-xs text-right text-default-500 tracking-wider',
      },
      {
        field: 'predicted',
        headerName: LABELS.predicted,
        flex: 1,
        minWidth: 88,
        cellClass: 'text-xs text-left font-semibold text-foreground',
        headerClass: 'text-xs text-right text-default-500 tracking-wider',
      },
    ],
    []
  );

  const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } = useAgGridStatePersistence('prediction-explainer-grid');
  const columnDefsWithSort = useMemo(() => mergeColumnDefs(costComparisonColDefs), [costComparisonColDefs, mergeColumnDefs]);
  const onGridReady = useCallback((params: { api: any }) => {
    augmentGridReady(params, (p) => {
      p?.api?.sizeColumnsToFit?.();
    });
  }, [augmentGridReady]);

  const summaryStats = [
    { label: LABELS.predictedCost, val: fmt(totalCost), primary: true },
    { label: LABELS.partsCost, val: fmt(partsCost) },
    { label: LABELS.labourHours, val: fmtHrs(labourHrs) },
    { label: LABELS.nearestMatches, val: allMatches.length },
  ];

  const renderRightColumn = () => {
    if (!hasAnswerContent) {
      return (
        <div className="flex h-full items-center justify-center rounded-lg bg-default-100 p-4">
          <p className="text-2xl font-medium text-default-500">{LABELS.notFound}</p>
        </div>
      );
    }
    if (isLossCode) {
      return (
        <>
          <h3 className="text-sm uppercase tracking-wider text-default-500 font-semibold mb-3">Loss Code Item</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-3 mt-3">
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
              <div className="text-xs sm:text-sm uppercase tracking-wider text-primary/70 mb-0.5 font-semibold">
                {LABELS.itemCode}
              </div>
              <div className="text-lg sm:text-xl font-bold tracking-tight text-primary">
                {itemCode}
              </div>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
              <div className="text-xs sm:text-sm uppercase tracking-wider text-primary/70 mb-0.5 font-semibold">
                {LABELS.itemName}
              </div>
              <div className="text-lg sm:text-xl font-bold tracking-tight text-primary">
                {itemName}
              </div>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <h3 className="text-sm uppercase tracking-wider text-default-500 font-semibold mb-3">Actual vs Predicted</h3>
        <div className="w-full min-w-0 max-w-full flex flex-col">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 w-full min-w-0">
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-2.5 sm:p-3">
              <div className="text-[10px] uppercase tracking-wider text-primary/70 mb-0.5 font-semibold">
                {LABELS.totalActual}
              </div>
              <div className="text-lg sm:text-xl font-bold tracking-tight text-primary">
                {fmt(actualTotalCost)}
              </div>
              <div className="text-[10px] text-default-500 mt-0.5 font-semibold">
                {LABELS.actualFromClaim}
              </div>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-2.5 sm:p-3">
              <div className="text-[10px] uppercase tracking-wider text-primary/70 mb-0.5 font-semibold">
                {LABELS.totalPredicted}
              </div>
              <div className="text-lg sm:text-xl font-bold tracking-tight text-primary">
                {fmt(totalCost)}
              </div>
              <div className="text-[10px] text-default-500 mt-0.5 font-semibold">
                {LABELS.fromTopMatch}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto -mx-1 min-w-0 w-full max-w-full doc-pe-cost-grid-horizontal-only">
            <style>{`
              .doc-pe-cost-grid-horizontal-only .ag-root-wrapper,
              .doc-pe-cost-grid-horizontal-only .ag-root-wrapper-body { border: none !important; }
              .doc-pe-cost-grid-horizontal-only .ag-cell,
              .doc-pe-cost-grid-horizontal-only .ag-header-cell {
                border-left: none !important;
                border-right: none !important;
                border-top: none !important;
              }
              .doc-pe-cost-grid-horizontal-only .ag-header-cell {
                border-bottom: 1px solid rgb(229 231 235) !important;
              }
              .doc-pe-cost-grid-horizontal-only .ag-header {
                border-bottom: none !important;
              }
              .doc-pe-cost-grid-horizontal-only .ag-row .ag-cell {
                border-bottom: 1px solid rgb(229 231 235) !important;
              }
              .doc-pe-cost-grid-horizontal-only .ag-layout-auto-height .ag-body-viewport,
              .doc-pe-cost-grid-horizontal-only .ag-layout-auto-height .ag-center-cols-viewport,
              .doc-pe-cost-grid-horizontal-only .ag-layout-auto-height .ag-center-cols-container {
                min-height: 0 !important;
              }
            `}</style>
            <div className="ag-theme-alpine w-full min-w-0 max-w-full" style={{ width: '100%' }}>
              <AgGridReact<CostComparisonRow>
                rowData={costComparisonRows}
                columnDefs={columnDefsWithSort}
                suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
                defaultColDef={{
                  sortable: false,
                  resizable: false,
                  suppressMovable: true,
                }}
                domLayout="autoHeight"
                pagination={false}
                suppressPaginationPanel
                headerHeight={36}
                rowHeight={36}
                getRowId={(p) => p.data.id}
                onGridReady={onGridReady}
                onGridSizeChanged={(params) => {
                  params.api.sizeColumnsToFit();
                }}
              />
            </div>
          </div>
          {labourRate && (
            <div className="bg-default-100 border border-default-200 rounded-md p-2.5  text-xs text-default-700 leading-relaxed my-3">
              {fmtHrs(labourHrs)} × ${labourRate.toFixed(2)}
              <br />= {fmt(labourCost)} labour
              <br />+ {fmt(partsCost)} parts
              <br />
              <span className="text-primary font-bold text-[13px]">= {fmt(totalCost)}</span>
            </div>
          )}
          <div className="p-2 bg-primary/10 border border-primary/20 rounded-md text-xs text-primary leading-snug">
            {LABELS.basis}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="flex h-full min-h-0 max-h-[90vh] flex-col overflow-hidden">
      {/* HEADER */}
      <ModalHeader className="flex flex-col gap-1 border-b border-default-200 bg-[#F9FAFB] px-3 py-3 sm:px-4 sm:py-3 md:px-6 md:py-4 rounded-t-2xl flex-shrink-0">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mr-1 sm:mr-2">
              <Icon icon="mdi:chart-box-outline" className="text-white w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm sm:text-base md:text-lg font-semibold text-foreground leading-tight">
                {isLossCode ? LABELS.titleLossCode : LABELS.title}
              </h2>
              {isLossCode && question && (
                <p className="text-xs text-default-500 mt-0.5  leading-snug break-words">
                  {question.policy_coverage_code} · {time}
                </p>
              )}
              {!isLossCode && question && (() => {
                const claimId = String(question.claim_number ?? question.claim_no ?? '').trim();
                return (
                  <p className="text-xs text-default-500 mt-0.5  leading-snug break-words">
                    {claimId ? `${t('Claim')} #${claimId} · ` : ''}
                    {t('Request')} #{question.request_id} · {question.product_name} {question.product_model} · {question.policy_coverage_code} · {time}
                  </p>
                );
              })()}
            </div>
          </div>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            className="flex-shrink-0"
            onPress={onClose}
            aria-label={LABELS.close}
          >
            <Icon icon="mdi:close" className="w-5 h-5" />
          </Button>
        </div>

        {!isLossCode && (
          <>
            {/* SUMMARY BAR */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap sm:gap-3 md:gap-4 mt-2 pt-2 border-t border-default-200">
              <div className="flex items-center gap-3 md:gap-4 flex-wrap">
                {summaryStats.map((s) => (
                  <div key={s.label} className="flex flex-col flex-shrink-0">
                    <span className="text-[10px] uppercase tracking-wider text-default-500">{s.label}</span>
                    <span className={`text-sm md:text-base font-bold  tracking-tight ${s.primary ? 'text-primary' : 'text-foreground'}`}>
                      {s.val}
                    </span>
                  </div>
                ))}
              </div>
              <div className="hidden sm:block w-px h-8 bg-default-300 flex-shrink-0 self-stretch" />
              <p className="w-full min-w-0 text-xs md:text-sm text-default-700 leading-snug sm:flex-1 sm:max-w-[480px]">
                {LABELS.basedOnClaims(allMatches.length)}
                {isFallback && <> {LABELS.relaxedFilter}</>}
              </p>
            </div>

            {/* FALLBACK BANNER */}
        {isFallback && (
          <div className="bg-amber-50 border border-amber-200/60 rounded-md py-2 px-3 mt-2 flex items-start gap-2 text-xs text-amber-700">
            <span className="flex-shrink-0">⚠</span>
            <span className="min-w-0 break-words leading-snug">
              {LABELS.fallbackBanner(question.product_model, question.product_name)}
            </span>
          </div>
        )}
          </>
        )}
      </ModalHeader>

      {/* 3-COLUMN BODY */}
      <ModalBody className="!p-0 flex-1 min-h-0 overflow-y-auto md:!overflow-hidden">
        <div className="flex flex-col md:grid md:grid-cols-[25%_45%_30%] h-auto md:h-full min-h-0 min-w-0 w-full">
          {/* LEFT — Input Claim */}
          <div className="p-3 sm:p-4 md:p-5 border-b md:border-b-0 md:border-r border-default-200 md:overflow-y-auto md:h-full md:min-h-0 min-w-0">
            <h3 className="text-sm uppercase tracking-wider text-default-500 font-semibold mb-3">
              {LABELS.inputClaim}
            </h3>
            {!question || typeof question !== 'object' ? (
              <div className="flex h-full items-center justify-center rounded-lg bg-default-100 p-4">
                <p className="text-2xl font-medium text-default-500">{LABELS.notFound}</p>
              </div>
            ) : (
              <>
                {hasHardFilterFields && (
                  <div className="mb-4">
                    <div className="text-[12px] font-bold uppercase tracking-wider text-primary mb-2 flex items-center gap-1.5">
                      {LABELS.hardFiltersLabel}
                    </div>
                    {HARD_FILTER_KEYS.map(
                      (k) =>
                        question[k] != null && (
                          <FilterPill
                            key={k}
                            label={k}
                            value={question[k]}
                            relaxed={k === PARTIAL_HARD_KEY && isFallback}
                            relaxedLabel={LABELS.relaxed}
                          />
                        ),
                    )}
                  </div>
                )}

                {hasHardFilterFields && hasSoftMatchFields && <div className="h-px bg-default-200 my-3" />}

                {hasSoftMatchFields && (() => {
                  const questionPartsList = formatPartsListFromChunk(question);
                  return (
                    <div>
                      <div className="text-[12px] font-bold uppercase tracking-wider text-primary mb-2 flex items-center gap-1.5">
                        {LABELS.softMatchesLabel}
                      </div>
                      {SOFT_KEYS.map((k) => (
                        <SoftBox key={k} label={formatLabel(k)} value={softFieldText(question, k)} />
                      ))}
                      {questionPartsList && (
                        <SoftBox label={t('Part descriptions')} value={questionPartsList} />
                      )}
                    </div>
                  );
                })()}
              </>
            )}
        </div>

        {/* MIDDLE — Nearest Historical Claims */}
        <div className="p-3 sm:p-4 border-b md:border-b-0 md:border-r border-default-200 md:overflow-y-auto md:h-full md:min-h-0 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <h3 className="text-sm uppercase tracking-wider text-default-500 font-semibold m-0">
              {LABELS.nearestHistoricalClaims}
            </h3>
            <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-primary/10 text-primary">
                {LABELS.candidates(allMatches.length)}
              </span>
              {allMatches.length > 0 && isFallback && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-50 text-amber-700">
                  ⚠ {LABELS.fallbackApplied}
                </span>
              )}
            </div>
          </div>
          {allMatches.length === 0 ? (
            <div className="flex h-full items-center justify-center rounded-lg bg-default-100 p-4">
              <p className="text-2xl font-medium text-default-500 text-center break-words">
                {retrievedContextsMessage || LABELS.notFound}
              </p>
            </div>
          ) : (
            <>
          <div className="flex flex-col gap-2">
            {allMatches.map((m: any, i: number) => (
              <MatchCard
                key={m.chunk_id}
                match={m}
                rank={i + 1}
                isTop={i === 0}
                question={question}
                isLossCode={isLossCode}
                showScore={rerankedChunkIds.has(m.chunk_id)}
              />
            ))}
          </div>

          {!isLossCode && <div className="mt-4 pt-3 border-t border-default-200">
            <h4 className="text-xs uppercase tracking-wider text-default-500 font-semibold mb-2">
              {LABELS.softMatchWeights}
            </h4>
            {softWeights.map((w) => (
              <WeightBar key={w.key} label={w.label} pct={w.pct} subLabel={w.sub} />
            ))}
            <p className="mt-2 p-2 bg-default-100 border border-default-200 rounded text-xs text-default-500 leading-snug">
              {LABELS.weightsNote}
            </p>
          </div>}
            </>
          )}
        </div>

        {/* RIGHT — Predicted & Actual Cost / Loss code Item */}
        <div className="p-3 sm:p-4 md:p-5 md:overflow-y-auto md:h-full md:min-h-0 min-w-0 w-full max-w-full">
          {renderRightColumn()}
        </div>
      </div>
      </ModalBody>
    </div>
  );
};

/* ── modal wrapper ───────────────────────────────────────────────────── */

interface PredictionExplainerModalProps {
  setViewExplainer: (v: boolean) => void;
  explainerAnswer: any;
}

const PredictionExplainerModal: React.FC<PredictionExplainerModalProps> = ({
  setViewExplainer,
  explainerAnswer,
}) => {
  const { isOpen, onOpen } = useDisclosure();
  useEffect(() => {
    onOpen();
  }, []);

  const hasContexts =
    (Array.isArray(explainerAnswer?.retrieved_contexts) &&
      explainerAnswer.retrieved_contexts.length > 0) ||
    (typeof explainerAnswer?.retrieved_contexts === 'string' &&
      explainerAnswer.retrieved_contexts.trim() !== '') ||
    (Array.isArray(explainerAnswer?.reranked_contexts) &&
      explainerAnswer.reranked_contexts.length > 0);
  const hasQuestion =
    explainerAnswer?.question != null && typeof explainerAnswer.question === 'object';
  const hasAnswer =
    explainerAnswer?.answer != null &&
    (typeof explainerAnswer.answer !== 'string' || explainerAnswer.answer.trim() !== '');
  const hasData = explainerAnswer && (hasContexts || hasQuestion || hasAnswer);

  return (
    <Modal
      id="prediction-explainer-modal"
      backdrop="blur"
      isOpen={isOpen}
      onClose={() => setViewExplainer(false)}
      isDismissable={false}
      isKeyboardDismissDisabled
      hideCloseButton
      size="5xl"
      classNames={{
        base: '!w-[90vw] !max-w-[90vw] !h-[90vh] !max-h-[90vh] rounded-2xl',
        wrapper: 'overflow-hidden',
        body: 'p-0 overflow-hidden !h-full min-h-0',
        header: 'p-0',
      }}
      scrollBehavior="inside"
    >
      <ModalContent className="h-full min-h-0 max-h-[90vh] flex flex-col">
        {hasData ? (
          <RepairCostExplainerContent data={explainerAnswer} onClose={() => setViewExplainer(false)} />
        ) : (
          <>
            <ModalHeader className="flex flex-col gap-1 border-b border-default-200">
              <div className="flex items-center justify-between w-full">
                <h2 className="text-xl font-semibold">{LABELS.title}</h2>
                <Button isIconOnly variant="light" onPress={() => setViewExplainer(false)}>
                  <Icon icon="mdi:close" className="w-5 h-5" />
                </Button>
              </div>
            </ModalHeader>
            <ModalBody className="p-6 sm:p-10">
              <div className="text-center">
                <div className="text-base sm:text-lg font-medium text-default-700 mb-4">
                  {LABELS.notAvailable}
                </div>
                <CtyButton
                  id="explainer-modal-close-btn"
                  onPress={() => setViewExplainer(false)}
                  variant="bordered"
                  size="lg"
                >
                  {LABELS.close}
                </CtyButton>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

/* ── exported trigger (info icon) ────────────────────────────────────── */

export const PredictionExplainerInfoIcon = ({ messageId }: { messageId?: string }) => {
  const [showModal, setShowModal] = useState(false);
  const [responseData, setResponseData] = useState<any>('');
  const [getExplainAnswerDetails, { isLoading: isFetching }] = useGetExplainAnswerDetailsMutation();

  const handleOpenExplainer = async () => {
    if (!messageId) return;
    try {
      const result = await getExplainAnswerDetails(messageId).unwrap();
      setResponseData(result);
      setShowModal(true);
    } catch (err) {
      CtyShowToast(checkType.error, (err as Error)?.message || LABELS.errorFetching);
    }
  };

  if (!messageId) return null;

  return (
    <>
      <Tooltip content={LABELS.openExplainer} placement="top" showArrow>
        <span className="inline-flex items-center cursor-pointer">
          <CtyButton
            isIconOnly
            size="sm"
            variant="light"
            className="min-w-6 w-6 h-6"
            onPress={handleOpenExplainer}
            isLoading={isFetching}
            aria-label={LABELS.openExplainer}
          >
            {isFetching ? null : (
              <Icon icon="mdi:information-outline" className="w-4 h-4 text-default-500" />
            )}
          </CtyButton>
        </span>
      </Tooltip>
      {showModal && messageId && (
        <PredictionExplainerModal setViewExplainer={setShowModal} explainerAnswer={responseData} />
      )}
    </>
  );
};

export default PredictionExplainerInfoIcon;